import { Injectable } from '@angular/core';
import { SETTINGS } from '../configs/common.settings';
import { normalizeRoleName, ROLES } from '../configs/permissions.config';
import { EncryptionService } from './encryption.service';
import { getItem, removeItem, setItem } from '../utils/safe-storage';

const ALLOWED_ROLES = new Set<string>([
  ROLES.ADMIN,
  ROLES.MANAGEMENT,
  ROLES.SUPERVISOR,
  ROLES.STAFF,
]);

/** CryptoJS AES ciphertext prefix (OpenSSL salted). */
const CIPHER_PREFIX = 'U2FsdGVk';

/**
 * Persists user role encrypted in storage (same pattern as tokens).
 * Plain-text values are migrated once; invalid/tampered values are rejected.
 */
@Injectable({
  providedIn: 'root',
})
export class RoleStorageService {
  constructor(private encryption: EncryptionService) {}

  setRole(roleName: string): void {
    const normalized = normalizeRoleName(roleName);
    if (!normalized || !ALLOWED_ROLES.has(normalized)) {
      return;
    }
    setItem(SETTINGS.USER_ROLE, this.encryption.encrypt(normalized));
  }

  getRole(): string {
    const raw = getItem(SETTINGS.USER_ROLE);
    if (!raw) {
      return '';
    }

    const fromCipher = this.decryptRole(raw);
    if (fromCipher) {
      return fromCipher;
    }

    // One-time migration from legacy plain-text storage (e.g. "ADMIN").
    const legacy = normalizeRoleName(raw);
    if (legacy && ALLOWED_ROLES.has(legacy)) {
      this.setRole(legacy);
      return legacy;
    }

    this.clearRole();
    return '';
  }

  clearRole(): void {
    removeItem(SETTINGS.USER_ROLE);
  }

  private decryptRole(ciphertext: string): string {
    if (!ciphertext.startsWith(CIPHER_PREFIX)) {
      return '';
    }
    try {
      const plain = this.encryption.decrypt(ciphertext);
      const normalized = normalizeRoleName(plain);
      return normalized && ALLOWED_ROLES.has(normalized) ? normalized : '';
    } catch {
      return '';
    }
  }
}
